import { AnalyticsClient, SignupState } from '../../types'
import { toPascal } from '../stringHelpers'

export interface CdmEventDetail {
  version?: string
  activity?: string
  screenPath?: string
  screenName: string
  controlName?: string
  controlDetail?: string
  screenMode?: string
  actionAuxParams?: string
  action:
    | 'ScreenDisplayed'
    | 'ControlButtonClicked'
    | 'ControlHyperLinkClicked'
    | 'ModalWindowDisplayed'
}

export interface CdmEvent {
  version: string
  dateTime: string
  eventDetailType: string
  eventCategory: string
  eventDetail: CdmEventDetail
}

export type PublishCdmEvents = (data: CdmEvent[]) => unknown

export class CdmOobeAnalyticsClient implements AnalyticsClient {
  pathname: string
  screenUrl: string
  baseScreenPath: string
  publishCdmEvents: PublishCdmEvents
  signupState: SignupState

  constructor(signupState: SignupState) {
    const {
      analytics,
      signupSession: { analyticsBaseScreenPathName }
    } = signupState

    const baseScreenPath = analyticsBaseScreenPathName || '/MfeOobeEnroll/'
    this.signupState = signupState
    this.pathname = ''
    this.screenUrl = ''
    this.baseScreenPath = baseScreenPath
    this.publishCdmEvents = analytics?.publishCdmEvents
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateState(signupState: SignupState) {
    this.signupState = signupState
  }

  publishClickEvent({
    eventTarget,
    linkId,
    linkPlacement
  }: {
    eventTarget?: EventTarget
    linkId?: string
    linkPlacement?: string
  }) {
    if (eventTarget instanceof Element) {
      this.handleElementClick(eventTarget)
    } else if (linkId || linkPlacement) {
      this.publishLink(linkId, linkPlacement)
    }
  }

  private handleElementClick(element: Element) {
    const nodeName = element.nodeName.toLowerCase()
    const role = element.getAttribute('role')
    const type = element.getAttribute('type')

    const parentButtonElement =
      nodeName != 'button' && nodeName != 'a'
        ? this.findParentButton(element)
        : undefined

    if (
      ['a', 'button'].includes(nodeName) ||
      role === 'button' ||
      ['checkbox', 'radio'].includes(type) ||
      parentButtonElement
    ) {
      const screenName = toPascal(
        element['screenName'] || this.getDefaultScreenName()
      )
      const screenPath = element['screenName']
        ? this.getModalScreenPath()
        : this.getScreenPath()
      let controlName = parentButtonElement
        ? toPascal(
            parentButtonElement.getAttribute('data-analyticsid') ||
              parentButtonElement.getAttribute('data-testid') ||
              parentButtonElement
                .getAttribute('aria-label')
                ?.replace(/\s+/g, '-') ||
              parentButtonElement.textContent?.replace(/\s+/g, '-')
          )
        : toPascal(
            element.getAttribute('data-analyticsid') ||
              element.getAttribute('data-testid') ||
              element.getAttribute('aria-label')?.replace(/\s+/g, '-') ||
              element.textContent?.replace(/\s+/g, '-')
          )

      const controlDetail = element['controlDetail']
      const action =
        nodeName === 'a' && element.hasAttribute('href')
          ? 'ControlHyperLinkClicked'
          : 'ControlButtonClicked'

      if (controlName) {
        controlName =
          controlName == 'CloseSymbolButton' ? 'CloseModal' : controlName
        this.publishEvent({
          screenPath,
          screenName,
          controlName,
          ...(controlDetail !== undefined && { controlDetail }),
          action
        })
      }
    }
  }

  publishDisplayEvent({
    pathname,
    screenName,
    screenMode,
    action,
    controlDetail,
    screenPath,
    actionAuxParams,
    screenUrl,
    controlName
  }: {
    pathname?: string
    screenName?: string
    screenMode?: string
    action?: string
    controlDetail?: string
    screenPath?: string
    screenUrl?: string
    actionAuxParams?: Record<string, string | undefined>
    controlName?: string
  }) {
    if (screenUrl) this.screenUrl = screenUrl
    if (pathname) {
      this.pathname = pathname + 'Step'
    }
    const newScreenName = toPascal(screenName || this.getDefaultScreenName())
    const newScreenPath = screenName
      ? this.getModalScreenPath()
      : this.getScreenPath()

    this.publishEvent(
      {
        screenPath: screenPath || newScreenPath,
        screenName: newScreenName,
        action: action == 'ModalWindowDisplayed' ? action : 'ScreenDisplayed',
        ...(screenMode && { screenMode }),
        ...(controlDetail && { controlDetail }),
        ...(controlName && { controlName })
      },
      actionAuxParams
    )
  }

  publishCustomEvent(event?: { linkID?: string; linkPlacement?: string }) {
    if (event?.linkID && event?.linkPlacement) {
      this.publishLink(event.linkID, event.linkPlacement)
    }
  }
  publishHoverEvent({ eventTarget }: { eventTarget?: EventTarget }) {
    if (eventTarget instanceof Element) {
      const screenName = toPascal(
        eventTarget['screenName'] || this.getDefaultScreenName()
      )
      const screenPath = eventTarget['screenName']
        ? this.getModalScreenPath()
        : this.getScreenPath()
      let controlName = toPascal(
        eventTarget.getAttribute('data-analyticsid') ||
          eventTarget.getAttribute('data-testid')
      )

      const controlDetail = eventTarget['controlDetail']
      const action = 'ControlButtonClicked'

      if (controlName) {
        controlName =
          controlName == 'CloseSymbolButton' ? 'CloseModal' : controlName
        this.publishEvent({
          screenPath,
          screenName,
          controlName,
          ...(controlDetail !== undefined && { controlDetail }),
          action
        })
      }
    }
  }

  private findParentButton(element: Element): Element {
    for (let i = 0; i < 5; i++) {
      if (element?.parentElement?.nodeName.toLowerCase() === 'button') {
        return element?.parentElement
      }
      element = element?.parentElement
    }
  }

  private getDefaultScreenName() {
    return (
      this.screenUrl.substring(this.screenUrl.lastIndexOf('/') + 1) ||
      toPascal(this.pathname.substring(this.pathname.lastIndexOf('/') + 1))
    )
  }

  private getScreenPath() {
    return (
      this.screenUrl.split('/').slice(0, -1).join('/') + '/' ||
      this.baseScreenPath
    )
  }

  private getModalScreenPath() {
    const screenName = this.getDefaultScreenName()
    return (
      this.screenUrl + '/' ||
      `${this.getScreenPath()}${screenName ? screenName + '/' : ''}`
    )
  }

  private requiredParams(
    auxParams: Record<string, string | undefined>
  ): string {
    const { userAccount, signupSession, subscription } = this.signupState
    const partialSubId = this.signupState.partialSubscription?.data?.id
    const params: Record<string, string | undefined> = {
      userID: userAccount?.data?.resourceId,
      deviceID: this.getDeviceId(),
      appInstanceID: signupSession?.appInstanceID,
      partialSubscriptionID: partialSubId ? `${partialSubId}` : undefined,
      subscriptionID: subscription?.data?.accountIdentifier,
      ...auxParams
    }
    return this.formatToQueryParams(params)
  }

  private formatToQueryParams(params: Record<string, string | undefined>) {
    const queryParams = Object.entries(params)
      .filter(([key, value]) => key && value !== undefined) // Include only existing values
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`) // Encode values
      .join('&') // Join with '&' to form query string

    return queryParams
  }

  private getDeviceId() {
    return (
      this.signupState.signupSession?.printerUuid ||
      this.signupState.partialSubscription?.data?.printer?.deviceUuid ||
      this.signupState.partialSubscription?.data?.partialPrinter?.deviceUuid
    )
  }

  private publishLink(linkId?: string, linkPlacement?: string) {
    if (linkId && linkPlacement) {
      const cdmEventData: CdmEventDetail = {
        screenPath: this.baseScreenPath,
        screenName: toPascal(linkPlacement),
        controlName: linkId,
        action: 'ControlButtonClicked'
      }

      this.publishEvent(cdmEventData)
    }
  }

  private publishEvent(
    eventDetail: CdmEventDetail,
    auxParams?: Record<string, string | undefined>
  ) {
    try {
      const fullCdmEvent = {
        version: '1.4.0',
        dateTime: new Date().toISOString(),
        eventDetailType:
          'com.hp.cdm.domain.telemetry.type.eventDetail.category.simpleUi.version.1',
        eventCategory: 'simpleUi',
        eventDetail: {
          version: '1.4.0',
          activity: 'InstantInk-v01',
          screenPath: this.baseScreenPath,
          ...eventDetail,
          actionAuxParams: this.requiredParams(auxParams)
        }
      }

      this.publishCdmEvents([fullCdmEvent])
    } catch (error) {
      console.error('CDM Event Error:', error)
    }
  }
}
